"use client";

import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  Stack,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { apiService } from "../../app/api";
import { handleAxiosError } from "../../helper/errorHandler";
import { API_FEATURE_LABELS } from "./featureMap";

interface ApiUsageItem {
  total_usage: number;
  unit: string | null;
}

type ApiUsageResponse = Record<string, ApiUsageItem>;

interface Props {
  user_id: string;
}

const TotalApiUsage = ({ user_id }: Props) => {
  const [data, setData] = useState<ApiUsageResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    start_date: null,
    end_date: null,
  });

  const handleChange = (key: "start_date" | "end_date", value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchUsage = useCallback(async () => {
    if (!user_id) return;

    try {
      setLoading(true);

      const res = await apiService.get(`/api-usage/${user_id}`, {
        params: {
          start_date: filters.start_date,
          end_date: filters.end_date,
        },
      });

      setData(res.data);
    } catch (err) {
      handleAxiosError(err);
    } finally {
      setLoading(false);
    }
  }, [user_id, filters]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header + Filters */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h6">Total API Usage</Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          {/* Start Date Filter */}
          <TextField
            label="Start Date"
            type="date"
            value={filters.start_date}
            onChange={(e) => handleChange("start_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split("T")[0],
            }}
            size="small"
          />

          {/* End Date Filter */}
          <TextField
            label="End Date"
            type="date"
            value={filters.end_date}
            onChange={(e) => handleChange("end_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split("T")[0],
            }}
            size="small"
          />

          <IconButton color="primary" onClick={fetchUsage} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {!data || Object.keys(data).length === 0 ? (
        <Typography color="text.secondary" align="center">
          No API usage found
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {Object.entries(data).map(([key, value]) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
                <CardContent>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {API_FEATURE_LABELS[key]}
                  </Typography>

                  <Typography variant="h4" fontWeight={600}>
                    {value.unit === "characters"
                      ? value.total_usage ?? 0
                      : (value.total_usage ?? 0).toFixed(2)}
                  </Typography>

                  {value.unit && (
                    <Chip label={value.unit} size="small" sx={{ mt: 1 }} />
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default TotalApiUsage;
